import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { apiClient } from "../../lib/api";

interface ContactFormProps {
  artworkId?: string;
  /** Pre-fills the message field on mount. */
  initialMessage?: string;
  /** Label rendered above the form fields. */
  headerText?: string;
  /** Label on the submit button. */
  submitLabel?: string;
  /** Confirmation shown after a successful submit. */
  successText?: string;
  /** Visible row count of the message textarea. */
  messageRows?: number;
}

export const ContactForm = ({
  artworkId,
  initialMessage = "",
  headerText = "or send a message",
  submitLabel = "Send Inquiry",
  successText = "Message sent. Kinsey will be in touch.",
  messageRows = 4,
}: ContactFormProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(initialMessage);

  // If the caller swaps the initialMessage (e.g. opens the form fresh for a
  // different piece), keep the field synced as long as the user hasn't yet
  // started typing their own message.
  useEffect(() => {
    setMessage((cur) => (cur === "" ? initialMessage : cur));
  }, [initialMessage]);

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.frontend.SubmitInquiry({
        Name: name,
        Email: email,
        Message: message,
        ArtworkID: artworkId ?? "",
      }),
    onSuccess: () => {
      setName("");
      setEmail("");
      setMessage("");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;
    mutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="text-left max-w-xl mx-auto space-y-4">
      {headerText && (
        <p className="text-xs uppercase-track text-[var(--color-kj-text-light)] text-center mb-2">
          {headerText}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
            Name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="mt-1 w-full bg-transparent border-b border-[var(--color-kj-border)] focus:border-[var(--color-kj-text)] outline-none py-2 text-sm transition-colors"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-1 w-full bg-transparent border-b border-[var(--color-kj-border)] focus:border-[var(--color-kj-text)] outline-none py-2 text-sm transition-colors"
          />
        </label>
      </div>
      <label className="block">
        <span className="text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
          Message
        </span>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          rows={messageRows}
          className="mt-1 w-full bg-transparent border border-[var(--color-kj-border)] focus:border-[var(--color-kj-text)] outline-none p-3 text-sm leading-relaxed resize-y transition-colors"
        />
      </label>

      <div className="flex flex-col items-center pt-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="font-display uppercase-track text-xs px-8 py-3 border border-[var(--color-kj-text)] hover:bg-[var(--color-kj-text)] hover:text-[var(--color-kj-bg)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {mutation.isPending ? "Sending…" : submitLabel}
        </button>
        {mutation.isSuccess && (
          <p className="mt-4 text-xs uppercase-track text-[var(--color-kj-accent)]">
            {successText}
          </p>
        )}
        {mutation.isError && (
          <p className="mt-4 text-xs uppercase-track text-[var(--color-kj-danger)]">
            Something went wrong. Please try again or email directly.
          </p>
        )}
      </div>
    </form>
  );
};
